import {
  Assets,
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  type FederatedPointerEvent,
  type Spritesheet,
  type Texture,
  type Ticker,
} from 'pixi.js';
import {
  BIOMES,
  isSlotUnlocked,
  slotGemPrice,
  type BiomeId,
  type WorldMapNode,
} from '@goodtime/shared';
import { SceneBase } from './SceneBase';
import { AssetLoader } from '../asset/AssetLoader';
import {
  centreOn,
  clampCamera,
  shareOnScreen,
  openingZoom,
  visibleRect,
  zoomAbout,
  type Camera,
  type Size,
} from '../logic/realm/camera';
import { worldSlots } from '../logic/realm/places';
import { CLEAR, PLATE_SHIFT, TOP_BAR, socialAnchor, zoomAnchor } from '../logic/realm/mapHud';
import { boxMeetsPolygon, plateFade, pullWorldPlate, type Pt } from '../logic/realm/worlds';
import { pickLevel, tilesIn, type Tile, type TileSet } from '../logic/realm/tiles';
import { pad, placeLabels, union, type Rect } from '../logic/realm/labels';
import { parseAmbient } from '../logic/realm/ambient';
import {
  RealmAmbient,
  type AmbientArt,
  type AmbientStats,
  type PieceState,
  type SeaMode,
} from '../widgets/realm/RealmAmbient';
import { BoardDialog, BrassButton, COLOURS, loadUiFonts, uiStyle } from '../widgets/board';
import { BezelButton, RealmPlaque, RealmSwitcher, displayText } from '../widgets/realm/RealmPieces';
import { WorldFloors } from '../widgets/realm/WorldFloors';
import { ambientAsset, ambientJson, forgetAmbient } from '../widgets/realm/AmbientFiles';

/** Art the app supplies, by URL: the HUD's and the plates'. */
export interface PaintedMapArt {
  bezel: string;
  arrow: string;
  lock: string;
  gem: string;
  friends: string;
  join: string;
  schedule: string;
}

/** Screen edges to keep clear (notches, the app's top bar). */
export interface MapInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PaintedMapOptions {
  art: PaintedMapArt;
  /** Each world's floor, by URL: the reel floor of its first slot. A
   *  world's board, the switcher while it names the world, and the name
   *  plates of the world's slots and places sit on it. The opening world's
   *  loads before the map shows; the rest when they're first needed, after
   *  the opening view's tiles. A world without one gets the boards' plain
   *  dark base. */
  floors?: Partial<Record<BiomeId, string>>;
  /** Shared art loaded by alias before the map shows: the painted button
   *  plates the boards' buttons sit on. */
  shared?: readonly { alias: string; src: string }[];
  unlockedSlotIds: readonly string[];
  /** The slot the player was last on: the map opens there. */
  currentNodeId: number;
  /** A slot's medallion, for the world boards. */
  slotIcon?: (node: WorldMapNode) => string | undefined;
  /** Screen edges to keep clear (notches, the app's top bar). */
  insets?: MapInsets;
  /** Ambient life (on the realm map, flag `realm_alive`): the folder
   *  holding ambient.json and its pieces, and the tiles of the painting with
   *  the movers painted out (the clean plate), drawn instead of the
   *  painting's own tiles once the pieces load. `files`: what the folder is
   *  known to hold besides ambient.json (the sheets' json and picture, the
   *  masks), started with it instead of after it; see {@link warmAmbient}. */
  ambient?: { base: string; tiles: TileSet; sea?: SeaMode; files?: readonly string[] };
  onPlayNode: (node: WorldMapNode) => void;
  onFriends: () => void;
  onJoinWithCode: () => void;
  onSchedule: () => void;
}

/** A press that moves further than this is a drag, not a tap. */
const DRAG_SLOP = 6;
/** How long a glide takes. */
export const FLY_MS = 650;
export { TOP_BAR, PLATE_SHIFT };
/** How long a name takes to fade in or out. */
const NAME_FADE_MS = 160;
/** The other worlds' floors wait for the opening view's tiles, or this
 *  long at most. */
const FLOORS_WAIT_MS = 2500;

/** A name on the map: a building's plate or a world's name. It belongs to
 *  a spot on the painting but moves to stay clear of the controls. */
export interface Placed {
  item: Container;
  /** The spot it belongs to, in master pixels. */
  mx: number;
  my: number;
  /** How far below the spot it hangs, in master pixels. */
  drop: number;
  /** Its own box at screen size, about its origin. */
  box: Rect;
  maxShift: number;
  tappable: boolean;
  /** Slide fully on screen for free while the spot is on screen (default:
   *  as `tappable`). A world map's plates leave it off: a plate pushed off
   *  its spot by the controls then counts the push against `maxShift`, and
   *  hides, rather than landing on a neighbouring building. */
  slide?: boolean;
  /** A world map's plate: its building's outline box, in master pixels. The plate fades with the share of that box on
   *  screen (worlds.ts plateFade), so a plate never labels a building that is mostly off screen. */
  outline?: Rect;
  /** A world map's plate: the other buildings' tap and outline shapes, in master pixels. A plate whose spot has just left the
   *  screen is pulled back on while its building is wholly in view (worlds.ts pullWorldPlate), unless that would put it on one of these. */
  pullClear?: (readonly Pt[])[];
  /** How much of full opacity it fades to while shown (1 unless `outline` says the building is mostly off screen). */
  fade?: number;
  /** Shown or hidden now; its alpha fades towards it. */
  shown: boolean;
  /** Not placed yet: its first placing doesn't fade. */
  fresh: boolean;
  /** The world whose floor it sits on (a slot's or a place's plate). */
  world?: number;
}

type ArtKey = keyof PaintedMapArt;

/**
 * A painted map: one big painting the player pans and zooms, with buildings
 * that open slots, their name plates, ambient life and the HUD. The realm
 * map (the overview of every world) is built on it, and each world's own
 * map will be. Drag to pan; pinch, the wheel or the zoom buttons to zoom;
 * it never zooms out past the painting. The painting loads as tiles, only
 * those on screen, at the coarsest level that is sharp (logic/realm/tiles).
 * The switcher along the bottom names a world (or a stop in it) and its
 * middle opens that world's board, all its slots.
 *
 * A subclass says where the buildings are and draws them ({@link buildPlates}),
 * where the map opens ({@link openingSpot}), what the switcher says and
 * where its arrows go, and adds its own names and controls through the
 * hooks below.
 */
export abstract class PaintedMapScene<
  O extends PaintedMapOptions = PaintedMapOptions,
> extends SceneBase {
  protected tiles: TileSet;
  protected unlocked: readonly string[];
  private readonly art = new Map<ArtKey, Texture | null>();
  protected readonly floors: WorldFloors;
  /** Worlds whose floors were asked for before the opening view's tiles
   *  were in: they load once those are. */
  private readonly floorWants = new Set<number>();
  private floorsOpen = false;
  private floorsTimer: ReturnType<typeof setTimeout> | null = null;
  /** The painting's size, in master pixels. */
  protected readonly map: Size;

  /** The painting and everything on it, in master pixels. */
  protected readonly world = new Container();
  private readonly tileLayer = new Container();
  /** Ambient life (on the realm: the sea, ships and boats, gulls, the
   *  serpent, the lighthouse, smoke, steam, the fountains and the clouds)
   *  goes here: over the painting, under the names, in master pixels so it
   *  pans and zooms with the map (widgets/realm/RealmAmbient). */
  readonly ambientLayer = new Container();
  /** The painting's own movement (the sea's ripple, a world map's trees),
   *  drawn as moving copies of the tiles: over them, under everything else
   *  on the painting. */
  protected readonly paintLayer = new Container();
  private ambientArt: AmbientArt | null = null;
  private ambientUrls: string[] = [];
  private ambient: RealmAmbient | null = null;
  /** The host covers the map (a dialog over it): ambient life pauses. */
  protected hostPaused = false;
  /** Names on the painting that aren't buildings' plates. */
  protected readonly labelLayer = new Container();
  /** The buildings' tap areas and plates. */
  protected readonly landmarkLayer = new Container();
  protected readonly hud = new Container();
  private switcher: RealmSwitcher | null = null;
  private zoomButtons: BezelButton[] = [];
  private social: BezelButton[] = [];
  private board: BoardDialog | null = null;
  private boardWorld = -1;
  /** The buildings' plates, placed with the other names. */
  protected plates: Placed[] = [];
  /** The controls over the map, on screen, that names keep clear of. */
  protected keepOut: Rect[] = [];
  /** True once the board's opening animation has finished. */
  private boardReady = false;
  /** Each board's running animation (opening or closing), so a board is
   *  only destroyed once it has finished. */
  private readonly boardMoves = new WeakMap<BoardDialog, Promise<void>>();

  protected cam: Camera = { x: 0, y: 0, zoom: 0.4 };
  protected view: Size = { w: 1, h: 1 };
  /** The world the switcher names, whose floor it sits on. */
  protected currentWorld: number;
  /** The press in progress moved the map: it opens nothing. */
  protected dragged = false;
  /** After the switcher's arrows pick a stop, it stays named until the
   *  player moves the map (near an edge the view's middle can be nearer
   *  another). */
  protected holdSwitcher = false;
  private downAt: { x: number; y: number } | null = null;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinch: { d: number; zoom: number } | null = null;
  private settleFrame = 0;
  private flyFrame = 0;
  /** Resolves once the opening view's tiles are in (or it's been long
   *  enough): {@link firstView}. */
  private readonly opened: Promise<void>;
  private markOpened: () => void = () => undefined;

  private readonly tileSprites = new Map<string, Sprite>();
  private readonly tileTextures = new Map<string, Texture>();
  private readonly pending = new Set<string>();
  private wanted = new Set<string>();

  private readonly onMove = (e: PointerEvent): void => this.pointerMove(e);
  private readonly onUp = (e: PointerEvent): void => this.pointerUp(e);
  private readonly onWheel = (e: WheelEvent): void => this.wheel(e);
  private readonly onTick = (t: Ticker): void => {
    this.fadeNames(t.deltaMS);
    this.ambient?.update(t.deltaMS, visibleRect(this.cam, this.view), this.cam.zoom);
    this.tick(t.deltaMS);
  };
  private readonly onVisibility = (): void => this.pauseAmbient();

  constructor(
    container: HTMLElement,
    protected readonly opts: O,
    /** The painting's tiles, until the clean plate takes over. */
    tiles: TileSet,
    /** The world the switcher names first. */
    openingWorld: number,
  ) {
    super(container, { drawDefaultBackground: false });
    this.opened = new Promise((resolve) => (this.markOpened = resolve));
    this.tiles = tiles;
    this.map = { w: this.tiles.width, h: this.tiles.height };
    this.unlocked = opts.unlockedSlotIds;
    this.currentWorld = openingWorld;
    this.floors = new WorldFloors(
      (w) => {
        const id = BIOMES[w]?.id;
        return id ? opts.floors?.[id] : undefined;
      },
      (w, texture) => this.floorArrived(w, texture),
    );
  }

  // ---- what a subclass supplies ----

  /** The scene's name in console warnings, written out because production
   *  builds shorten class names. */
  protected abstract readonly logName: string;

  /** Draw the buildings' tap areas and plates afresh, into
   *  {@link landmarkLayer} and {@link plates}: once built, and again when
   *  the player's open slots change. */
  protected abstract buildPlates(): void;

  /** Where the map opens, as fractions of the painting. */
  protected abstract openingSpot(): { x: number; y: number };

  /** A building's middle by its slot's name, as fractions of the painting;
   *  null when there's none (the test hook {@link landmarkPoint}). */
  protected abstract buildingAt(name: string): { x: number; y: number } | null;

  /** Whether the map has a switcher along the bottom: the realm's names
   *  the world in view; a world's map has none. */
  protected hasSwitcher(): boolean {
    return true;
  }

  /** The switcher's arrows: one stop on (1) or back (-1). */
  protected stepSwitcher(_step: number): void {}

  /** What the switcher says: its title and the line under it. */
  protected switcherText(): [title: string, line: string] {
    return ['', ''];
  }

  /** Build the subclass's own names, layers and controls, after the
   *  buildings and the HUD and before ambient life. */
  protected buildMore(): void {}

  /** More names to place with the buildings' plates, after them. */
  protected moreNames(): readonly Placed[] {
    return [];
  }

  /** The zoom the map opens at: on a landscape screen the whole painting
   *  across; on a portrait one, about a world's width (the realm map). */
  protected openingZoom(): number {
    return openingZoom(this.map, this.view);
  }

  /** The screen changed size (first: the first time). */
  protected resized(_first: boolean): void {}

  /** Lay out the subclass's own controls; returns them in groups, which
   *  names keep clear of. */
  protected layoutMore(_w: number, _h: number, _inset: MapInsets): Container[][] {
    return [];
  }

  /** The subclass's own controls, by id, for {@link controlPoint}. */
  protected moreControls(_out: Map<string, Container>): void {}

  /** Escape was pressed: close the subclass's own card, if one is up. */
  protected escapeOverlay(): boolean {
    return false;
  }

  /** Something of the subclass's own covers the map: ambient life pauses
   *  and the wheel doesn't zoom. */
  protected covering(): boolean {
    return false;
  }

  /** The host covered or uncovered the map. */
  protected hostCovered(_covered: boolean): void {}

  /** The camera moved, to this zoom. */
  protected cameraMoved(_zoom: number): void {}

  /** The camera came to rest over this spot (fractions of the painting),
   *  unless the switcher holds its stop. */
  protected viewCentred(_x: number, _y: number): void {}

  /** A world's floor arrived (after the switcher, the board and the
   *  plates took it). */
  protected floorIn(_world: number, _texture: Texture): void {}

  /** Free the subclass's own pieces. */
  protected disposeMore(): void {}

  /** A frame went by, `ms` long. */
  protected tick(_ms: number): void {}

  /** The ambient life has been built (after {@link buildMore}). */
  protected ambientMade(): void {}

  /** The tiles on screen changed (`level/col_row` to the sprite drawing
   *  it), for anything that draws a copy of them. */
  protected tilesChanged(_tiles: ReadonlyMap<string, Sprite>): void {}

  // ---- camera and input hooks: a subclass with its own camera rules (a
  // strip that only pans along one axis, say) overrides these; the defaults
  // are the realm map's ----

  /** Keep a camera within what the view may show; every camera the scene
   *  applies or glides to passes through this. By default the painting
   *  covers the screen at a zoom it allows ({@link clampCamera}). */
  protected clampView(cam: Camera): Camera {
    return clampCamera(cam, this.map, this.view);
  }

  /** A drag moved the pointer by this many screen pixels; by default the
   *  camera follows on both axes. */
  protected dragBy(dx: number, dy: number): void {
    this.apply({ ...this.cam, x: this.cam.x + dx, y: this.cam.y + dy });
  }

  /** Two fingers zoom the map (default yes). */
  protected allowsPinch(): boolean {
    return true;
  }

  /** The wheel turned over this screen point (the scene is uncovered and
   *  any glide has been stopped); by default it zooms about the point. */
  protected wheelAt(e: WheelEvent, at: { x: number; y: number }): void {
    const factor = e.ctrlKey ? Math.exp(-e.deltaY * 0.01) : e.deltaY < 0 ? 1.15 : 1 / 1.15;
    this.apply(zoomAbout(this.cam, at.x, at.y, factor, this.map, this.view));
  }

  /** The last pointer was lifted (or cancelled). `this.dragged` says whether
   *  the press moved (a subclass reads it here). Nothing happens by default. */
  protected pressEnded(): void {}

  /** Whether the zoom buttons are built (default: on a fine pointer, a
   *  mouse). Read once, as the HUD is built. */
  protected wantsZoomButtons(): boolean {
    return window.matchMedia?.('(pointer: fine)').matches ?? false;
  }

  /** How far past the screen's edges tiles are kept ready, in master
   *  pixels at this zoom (default: 128 screen pixels on both axes). */
  protected tileMargin(zoom: number): { x: number; y: number } {
    return { x: 128 / zoom, y: 128 / zoom };
  }

  /** The camera the map opens with, once its size is known (default: centred
   *  on {@link openingSpot} at {@link openingZoom}). */
  protected openingCamera(): Camera {
    const at = this.openingSpot();
    return centreOn(at.x * this.map.w, at.y * this.map.h, this.openingZoom(), this.map, this.view);
  }

  /** How far names keep from the screen's edges (default {@link CLEAR}). */
  protected edgeClear(): number {
    return CLEAR;
  }

  /** The switcher's middle was tapped: by default, the world's board. */
  protected switcherTapped(): void {
    this.openBoard(this.currentWorld);
  }

  /** Room kept to the switcher's right for a control of the subclass's,
   *  in screen pixels. */
  protected switcherSide(): number {
    return 0;
  }

  /** How far down the social buttons start below their usual place, to
   *  make room for a control of the subclass's above them. */
  protected socialDrop(): number {
    return 0;
  }

  // ---- the open slots ----

  /** The player's open slots changed (a gem unlock). */
  setUnlockedSlotIds(ids: readonly string[]): void {
    this.unlocked = ids;
    if (this.state !== 'initialized') return;
    this.buildPlates();
    this.placeNames();
    this.updateSwitcher();
    if (this.boardUp()) this.openBoard(this.boardWorld);
  }

  protected override async preload(): Promise<void> {
    const entries = Object.entries(this.opts.art) as [ArtKey, string][];
    await Promise.all([
      loadUiFonts(),
      AssetLoader.shared.loadTextures(this.opts.shared ?? []).catch(() => undefined),
      ...entries.map(async ([key, url]) => {
        this.art.set(key, await Assets.load<Texture>(url).catch(() => null));
      }),
      // The opening world's floor: its switcher shows at once.
      this.floors.load(this.currentWorld),
      this.loadAmbient(),
    ]);
  }

  /** A piece of the app's art, or null when it didn't load. */
  protected texture(key: ArtKey): Texture | null {
    return this.art.get(key) ?? null;
  }

  // ---- the worlds' floors ----

  /** A world's floor if it has loaded; else null, and it loads (after the
   *  opening view's tiles) and {@link floorArrived} puts it in place. */
  protected floorFor(world: number): Texture | null {
    const t = this.floors.loaded(world);
    if (!t) this.wantFloor(world);
    return t;
  }

  private wantFloor(world: number): void {
    if (this.floors.loaded(world)) return;
    if (this.floorsOpen) void this.floors.load(world);
    else this.floorWants.add(world);
  }

  /** The opening view's tiles are in (or it's been long enough): load the
   *  floors asked for meanwhile. */
  private openFloors(): void {
    if (this.floorsOpen || this.state === 'destroyed') return;
    this.floorsOpen = true;
    this.markOpened();
    if (this.floorsTimer) clearTimeout(this.floorsTimer);
    this.floorsTimer = null;
    for (const w of this.floorWants) void this.floors.load(w);
    this.floorWants.clear();
  }

  private floorArrived(world: number, texture: Texture): void {
    if (this.state === 'destroyed') return;
    if (world === this.currentWorld) this.switcher?.setFloor(texture);
    if (this.board && world === this.boardWorld) this.board.setFloor(texture);
    for (const p of this.plates) {
      if (p.world === world && p.item instanceof RealmPlaque) p.item.setFloor(texture);
    }
    this.floorIn(world, texture);
  }

  /** The ambient pieces. Only once they're in does the map switch to the
   *  clean plate, so a failed load still shows the whole painting. */
  private async loadAmbient(): Promise<void> {
    const opt = this.opts.ambient;
    if (!opt) return;
    try {
      // Everything the folder is known to hold starts with ambient.json (or
      // already has, from the overview's glide); ambient.json then says
      // which of it the map uses, and whatever it names beyond the hints
      // loads after it, as before.
      const hinted = (opt.files ?? []).map((f) => `${opt.base}/${f}`);
      this.ambientUrls = [...hinted];
      for (const u of hinted) void ambientAsset(u).catch(() => undefined);
      const spec = parseAmbient(await ambientJson(opt.base));
      const sheetUrls = spec.sheets.map((s) => `${opt.base}/${s}`);
      // The sea's masks, and the trees' (a world map's sway).
      const masked = [
        ...spec.water,
        ...spec.elements.flatMap((e) => (e.kind === 'sway' ? [e] : [])),
      ];
      const maskUrls = masked.map((w) => `${opt.base}/${w.mask}`);
      this.ambientUrls = [...new Set([...sheetUrls, ...maskUrls, ...hinted])];
      const [sheets, masks] = await Promise.all([
        Promise.all(sheetUrls.map((u) => ambientAsset<Spritesheet>(u))),
        Promise.all(maskUrls.map((u) => ambientAsset<Texture>(u))),
      ]);
      const frames: Record<string, Texture> = {};
      for (const sheet of sheets) Object.assign(frames, sheet.textures);
      const masksById: Record<string, Texture> = {};
      masked.forEach((w, i) => {
        const m = masks[i];
        if (m) masksById[w.id] = m;
      });
      this.ambientArt = { spec, frames, masks: masksById };
      this.tiles = opt.tiles;
    } catch (err) {
      console.warn(`[${this.logName}] ambient life off:`, err);
      this.ambientArt = null;
    }
  }

  protected buildContent(): void {
    const stage = this.app.stage;
    this.tileLayer.sortableChildren = true;
    this.world.addChild(
      this.tileLayer,
      this.paintLayer,
      this.ambientLayer,
      this.labelLayer,
      this.landmarkLayer,
    );
    this.world.eventMode = 'static';
    this.world.hitArea = new Rectangle(0, 0, this.map.w, this.map.h);
    this.world.on('pointerdown', (e: FederatedPointerEvent) => this.pointerDown(e));
    stage.addChild(this.world, this.hud);

    this.buildPlates();
    this.buildHud();
    this.buildMore();
    if (this.ambientArt) {
      const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      this.ambient = new RealmAmbient(this.ambientLayer, this.ambientArt, {
        still,
        sea: this.opts.ambient?.sea,
        under: this.paintLayer,
      });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.pauseAmbient();
      this.ambientMade();
    }

    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
    this.app.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    this.app.canvas.style.touchAction = 'none';
    this.app.ticker.add(this.onTick);
    this.floorsTimer = setTimeout(() => this.openFloors(), FLOORS_WAIT_MS);
  }

  protected override onResize(): void {
    const first = this.view.w === 1;
    this.view = { w: this.app.screen.width || 1, h: this.app.screen.height || 1 };
    this.layoutHud();
    this.resized(first);
    if (first) {
      this.apply(this.openingCamera());
      this.settle();
    } else {
      this.apply(this.cam);
    }
    this.board?.layout(this.view.w, this.view.h);
  }

  protected override onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.escapeOverlay()) return;
    if (event.key === 'Escape' && this.boardUp()) this.closeBoard();
    else if (event.key === '+' || event.key === '=') this.zoomBy(1.4);
    else if (event.key === '-') this.zoomBy(1 / 1.4);
  }

  protected disposeResources(wasInitialized: boolean): void {
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.disposeMore();
    if (this.floorsTimer) clearTimeout(this.floorsTimer);
    this.floorsTimer = null;
    // Freed once the stage (which still draws them) has gone with the app.
    const floors = this.floors;
    setTimeout(() => floors.destroy(), 0);
    this.ambient?.destroy();
    this.ambient = null;
    if (this.opts.ambient) forgetAmbient(this.opts.ambient.base, this.ambientUrls);
    this.ambientUrls = [];
    if (wasInitialized) {
      this.app.canvas.removeEventListener('wheel', this.onWheel);
      this.app.ticker.remove(this.onTick);
    }
    if (this.settleFrame) cancelAnimationFrame(this.settleFrame);
    this.stopFly();
    // Free the painting: tiles are big, and the slot is next.
    for (const key of this.tileTextures.keys())
      void Assets.unload(this.tileUrl(key)).catch(() => undefined);
    this.tileTextures.clear();
    this.wanted.clear();
  }

  // ---- test and dev hooks (screen points are CSS pixels in the canvas) ----

  /** Where a painted building's middle is on screen. */
  landmarkPoint(name: string): { x: number; y: number } | null {
    const l = this.buildingAt(name);
    if (!l) return null;
    return {
      x: this.cam.x + l.x * this.map.w * this.cam.zoom,
      y: this.cam.y + l.y * this.map.h * this.cam.zoom,
    };
  }

  /** Where a HUD control is: prev, next, world, friends, join, schedule,
   *  zoomIn, zoomOut, the subclass's own, or a board's `play:<slot>` and
   *  `close`. */
  controlPoint(id: string): { x: number; y: number } | null {
    const target = this.controls().get(id);
    if (!target || !target.visible || !target.parent?.visible) return null;
    const b = target.getBounds();
    return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
  }

  /** The world named along the bottom. */
  worldName(): string {
    return BIOMES[this.currentWorld]?.name ?? '';
  }

  /** The open board's world, or null. */
  boardName(): string | null {
    return this.board?.visible && this.boardReady ? (BIOMES[this.boardWorld]?.name ?? null) : null;
  }

  /** Which world's floor the switcher, the open board and each slot's
   *  plate sit on, by world id; null where there's none yet (tests). */
  floorsUi(): {
    switcher: string | null;
    board: string | null;
    plaques: Record<string, string | null>;
  } {
    const id = (t: Texture | null): string | null => {
      const w = this.floors.worldOf(t);
      return w === null ? null : (BIOMES[w]?.id ?? null);
    };
    const plaques: Record<string, string | null> = {};
    for (const p of this.plates) {
      if (p.item instanceof RealmPlaque) plaques[p.item.label] = id(p.item.floorTexture);
    }
    return {
      switcher: id(this.switcher?.floorTexture ?? null),
      board: this.boardUp() ? id(this.board?.floorTexture ?? null) : null,
      plaques,
    };
  }

  /** Resolves once the opening view's tiles have loaded (or a moment has
   *  passed): the host can fade the map in then. */
  firstView(): Promise<void> {
    return this.opened;
  }

  /** The camera is gliding (the switcher's arrows, a zoom button). */
  gliding(): boolean {
    return this.flyFrame !== 0;
  }

  /** Tiles on screen, as `level/col_row`. */
  tileKeys(): string[] {
    return [...this.tileSprites.keys()];
  }

  /** The ambient layer's state, or null when it's off. */
  ambientStats(): AmbientStats | null {
    return this.ambient?.stats() ?? null;
  }

  /** Every ambient piece's state (the browser tests), or null when off. */
  ambientPieces(): PieceState[] | null {
    return this.ambient?.pieces() ?? null;
  }

  /** Switch the sea's motion by hand (the frame-rate checks). */
  setAmbientSea(mode: 'ripple' | 'highlights'): void {
    this.ambient?.setSea(mode);
  }

  /** The locked buildings' levels by slot (1 locked, 0 open): the life
   *  that belongs to each goes dark or grey with it. */
  protected setAmbientLocks(levels: ReadonlyMap<number, number>): void {
    this.ambient?.setLocks(levels);
  }

  /** Switch the trees' sway on or off by hand (the frame-rate checks). */
  setAmbientSway(on: boolean): void {
    this.ambient?.setSway(on);
  }

  /** The host covers the map, or uncovers it: ambient life pauses. */
  setAmbientPaused(paused: boolean): void {
    this.hostPaused = paused;
    this.hostCovered(paused);
    this.pauseAmbient();
  }

  /** Glide to a spot on the painting (master pixels) at a zoom. */
  focus(mx: number, my: number, zoom: number, glide = true): void {
    const to = centreOn(mx, my, zoom, this.map, this.view);
    this.holdSwitcher = false;
    if (glide) this.flyTo(to);
    else this.apply(to);
  }

  protected pauseAmbient(): void {
    this.ambient?.setPaused(this.mapCovered());
  }

  /** Nobody can see the map: the tab is hidden, or something covers it. */
  protected mapCovered(): boolean {
    return document.hidden || this.hostPaused || this.boardUp() || this.covering();
  }

  // ---- the camera ----

  protected apply(next: Camera): void {
    this.cam = this.clampView(next);
    this.world.position.set(this.cam.x, this.cam.y);
    this.world.scale.set(this.cam.zoom);
    this.cameraMoved(this.cam.zoom);
    this.placeNames();
    if (!this.settleFrame) this.settleFrame = requestAnimationFrame(() => this.settle());
  }

  /** Put each name by its spot, fully on screen and clear of the controls
   *  and of each other; hide it (with a short fade) when its spot is off
   *  screen or it would have to move too far. Names are drawn at screen
   *  size whatever the zoom. */
  protected placeNames(): void {
    const { x: cx, y: cy, zoom } = this.cam;
    const all = this.names();
    // A world map's plate fades with the share of its building on screen (and is hidden below the cut-off).
    const shares = all.map((p) => (p.outline ? shareOnScreen(p.outline, this.cam, this.view) : 1));
    const fades = shares.map((share, i) => (all[i]!.outline ? plateFade(share) : 1));
    // The plates pulled back onto the screen, and what each must keep off (the other buildings, in screen pixels).
    const pulledClear = new Map<number, () => Pt[][]>();
    const places = placeLabels(
      all.map((p, i) => {
        let spot = { x: cx + p.mx * zoom, y: cy + p.my * zoom };
        let want = {
          x: spot.x + p.box.x,
          y: spot.y + p.drop * zoom + p.box.y,
          w: p.box.w,
          h: p.box.h,
        };
        if (p.outline && p.pullClear) {
          // Its building is whole on screen but the plate's spot has just left it: slide the plate onto the screen.
          const clear = (): Pt[][] => p.pullClear!.map((poly) => poly.map(([x, y]): Pt => [cx + x * zoom, cy + y * zoom]));
          const pulled = pullWorldPlate(
            want,
            spot,
            this.view,
            CLEAR,
            p.maxShift,
            shares[i]!,
            this.keepOut,
            clear,
          );
          if (pulled) {
            ({ want, spot } = pulled);
            pulledClear.set(i, clear);
          }
        }
        return {
          spot,
          want,
          maxShift: p.maxShift,
          slide: p.slide ?? p.tappable,
          // A pulled plate is placed after the others, so it never moves a plate that hung by its own spot.
          group: p.tappable && !pulledClear.has(i) ? 0 : 1,
        };
      }),
      this.view,
      this.keepOut,
      this.edgeClear(),
    );
    const snap = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    all.forEach((p, i) => {
      const { at } = places[i]!;
      const fade = fades[i]!;
      // A pulled plate that other plates left on another building stays hidden.
      const onOther = pulledClear.get(i)?.().some((poly) => boxMeetsPolygon(at, poly)) ?? false;
      const shown = places[i]!.shown && fade > 0 && !onOther;
      p.fade = fade;
      p.item.scale.set(1 / zoom);
      p.item.position.set((at.x - p.box.x - cx) / zoom, (at.y - p.box.y - cy) / zoom);
      p.shown = shown;
      if (shown && p.world !== undefined) this.wantFloor(p.world);
      if (p.fresh || snap) {
        p.fresh = false;
        p.item.alpha = shown ? fade : 0;
        p.item.visible = shown;
      }
      if (p.tappable) p.item.eventMode = shown && fade >= 0.5 ? 'static' : 'none';
    });
  }

  /** Every name on the map: the buildings' plates, then the subclass's. */
  private names(): Placed[] {
    return [...this.plates, ...this.moreNames()];
  }

  private fadeNames(ms: number): void {
    const step = ms / NAME_FADE_MS;
    for (const p of this.names()) {
      const target = p.shown ? (p.fade ?? 1) : 0;
      const a = p.item.alpha;
      if (a === target) continue;
      p.item.alpha = a < target ? Math.min(target, a + step) : Math.max(target, a - step);
      p.item.visible = p.item.alpha > 0;
    }
  }

  private stopFly(): void {
    if (this.flyFrame) cancelAnimationFrame(this.flyFrame);
    this.flyFrame = 0;
  }

  protected flyTo(target: Camera, ms: number = FLY_MS): void {
    this.stopFly();
    const from = this.cam;
    const to = this.clampView(target);
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.apply(to);
      return;
    }
    const t0 = performance.now();
    const step = (now: number): void => {
      const t = Math.min(1, (now - t0) / ms);
      const e = 1 - (1 - t) ** 3;
      this.apply({
        x: from.x + (to.x - from.x) * e,
        y: from.y + (to.y - from.y) * e,
        zoom: from.zoom + (to.zoom - from.zoom) * e,
      });
      this.flyFrame = t < 1 ? requestAnimationFrame(step) : 0;
    };
    this.flyFrame = requestAnimationFrame(step);
  }

  protected zoomBy(factor: number): void {
    this.flyTo(zoomAbout(this.cam, this.view.w / 2, this.view.h / 2, factor, this.map, this.view));
  }

  // ---- the painting ----

  private tileUrl(key: string): string {
    return `${this.tiles.base}/${key}.webp`;
  }

  /** Which tiles to draw: the one-tile base, any coarser tiles already
   *  loaded under the view (so nothing flashes while sharper ones load),
   *  then the level the zoom wants. Drops the rest, and their textures. */
  private settle(): void {
    this.settleFrame = 0;
    if (this.state === 'destroyed') return;
    const c = this.cam;
    const rect = visibleRect(c, this.view);
    const margin = this.tileMargin(c.zoom);
    const padded = {
      x: rect.x - margin.x,
      y: rect.y - margin.y,
      w: rect.w + 2 * margin.x,
      h: rect.h + 2 * margin.y,
    };
    const level = pickLevel(this.tiles, c.zoom, window.devicePixelRatio || 1, padded);
    const next: Tile[] = [...tilesIn(this.tiles, 0, padded)];
    for (let l = 1; l < level; l++) {
      next.push(...tilesIn(this.tiles, l, padded).filter((t) => this.tileTextures.has(t.key)));
    }
    if (level > 0) next.push(...tilesIn(this.tiles, level, padded));
    this.wanted = new Set(next.map((t) => t.key));
    for (const t of next) this.showTile(t);
    const dropped: string[] = [];
    for (const [key, sprite] of this.tileSprites) {
      if (this.wanted.has(key)) continue;
      sprite.destroy();
      this.tileSprites.delete(key);
      if (!key.startsWith('0/')) dropped.push(key);
    }
    // The copies of the tiles let go of them before they're unloaded.
    this.ambient?.syncTiles(this.tileSprites);
    this.tilesChanged(this.tileSprites);
    for (const key of dropped) {
      this.tileTextures.delete(key);
      void Assets.unload(this.tileUrl(key)).catch(() => undefined);
    }
    const mid = { x: (rect.x + rect.w / 2) / this.map.w, y: (rect.y + rect.h / 2) / this.map.h };
    if (!this.flyFrame && !this.holdSwitcher) this.viewCentred(mid.x, mid.y);
  }

  private showTile(t: Tile): void {
    if (this.tileSprites.has(t.key)) return;
    const texture = this.tileTextures.get(t.key);
    if (texture) {
      const s = new Sprite(texture);
      s.position.set(t.x, t.y);
      s.width = t.w;
      s.height = t.h;
      s.zIndex = t.level;
      this.tileLayer.addChild(s);
      this.tileSprites.set(t.key, s);
      this.ambient?.syncTiles(this.tileSprites);
      this.tilesChanged(this.tileSprites);
      return;
    }
    if (this.pending.has(t.key)) return;
    this.pending.add(t.key);
    const url = this.tileUrl(t.key);
    void Assets.load<Texture>(url)
      .then((tex) => {
        this.pending.delete(t.key);
        if (this.state === 'destroyed') {
          void Assets.unload(url).catch(() => undefined);
          return;
        }
        this.tileTextures.set(t.key, tex);
        if (this.pending.size === 0) this.openFloors();
        if (this.wanted.has(t.key)) this.showTile(t);
        else if (t.level > 0) {
          this.tileTextures.delete(t.key);
          void Assets.unload(url).catch(() => undefined);
        }
      })
      .catch(() => {
        this.pending.delete(t.key);
        if (this.pending.size === 0) this.openFloors();
      });
  }

  // ---- the buildings ----

  /** A building (or its plate) was tapped: play its slot, unless the press
   *  was a drag. */
  protected tapSlot(node: WorldMapNode): void {
    if (this.dragged) return;
    this.opts.onPlayNode(node);
  }

  // ---- the HUD ----

  private buildHud(): void {
    const tex = (k: ArtKey): Texture | null => this.texture(k);
    this.switcher = this.hasSwitcher()
      ? new RealmSwitcher(this.floors.loaded(this.currentWorld), tex('arrow'), {
          prev: () => this.stepSwitcher(-1),
          next: () => this.stepSwitcher(1),
          open: () => this.switcherTapped(),
        })
      : null;
    this.social = [
      new BezelButton(tex('bezel'), tex('friends'), 'Friends', () => this.opts.onFriends()),
      new BezelButton(tex('bezel'), tex('join'), 'Join', () => this.opts.onJoinWithCode()),
      new BezelButton(tex('bezel'), tex('schedule'), 'Schedule', () => this.opts.onSchedule()),
    ];
    if (this.wantsZoomButtons()) {
      this.zoomButtons = [
        new BezelButton(tex('bezel'), displayText('−', 26), 'Zoom out', () => this.zoomBy(1 / 1.4)),
        new BezelButton(tex('bezel'), displayText('+', 26), 'Zoom in', () => this.zoomBy(1.4)),
      ];
    }
    if (this.switcher) this.hud.addChild(this.switcher);
    this.hud.addChild(...this.social, ...this.zoomButtons);
    this.updateSwitcher();
  }

  protected layoutHud(): void {
    const inset = this.opts.insets ?? { top: 0, right: 0, bottom: 0, left: 0 };
    const { w, h } = this.view;
    const drop = this.socialDrop();
    this.social.forEach((b, i) => b.position.set(...socialAnchor(inset, i, drop)));
    if (this.switcher) {
      const side = this.switcherSide();
      const sw = Math.min(380, w - 28 - side);
      this.switcher.layout(sw);
      this.switcher.position.set((w - sw - side) / 2, h - inset.bottom - 14 - 56);
    }
    this.zoomButtons.forEach((b, i, all) => b.position.set(...zoomAnchor(this.view, inset, i, all.length)));
    const more = this.layoutMore(w, h, inset);
    // Names keep clear of the app's top bar and of each group of controls.
    const groups: Container[][] = [
      this.social,
      this.zoomButtons,
      this.switcher ? [this.switcher] : [],
      ...more,
    ];
    this.keepOut = [
      { x: 0, y: 0, w, h: inset.top + TOP_BAR },
      ...groups
        .map((g) => union(g.map((c) => rectOf(c.getBounds()))))
        .filter((r): r is Rect => r !== null)
        .map((r) => pad(r, CLEAR)),
    ];
  }

  /** Where the switcher is on screen, or null without one. */
  protected switcherRect(): Rect | null {
    if (!this.switcher) return null;
    return { x: this.switcher.x, y: this.switcher.y, w: this.switcher.boardWidth, h: 56 };
  }

  protected updateSwitcher(): void {
    // The floor of the world it names (until that loads, the last one's).
    const floor = this.floorFor(this.currentWorld);
    if (floor) this.switcher?.setFloor(floor);
    const [title, line] = this.switcherText();
    this.switcher?.set(title, line);
  }

  /** A world's board: every slot in it, PLAY on the open ones, the gem
   *  price on the locked ones (which opens the unlock). */
  protected openBoard(world: number): void {
    if (this.board) this.retireBoard(this.board);
    this.boardWorld = world;
    const board = new BoardDialog({
      title: BIOMES[world]?.name ?? '',
      floor: this.floorFor(world),
      maxWidth: 380,
    });
    this.board = board;
    board.layout(this.view.w, this.view.h);
    const width = board.contentWidth;
    const lock = this.texture('lock');
    const gem = this.texture('gem');
    const ROW = 50;
    let y = 0;
    for (const node of worldSlots(world)) {
      const open = isSlotUnlocked(node.id, this.unlocked);
      const row = new Container();
      row.position.set(0, y);
      const medallion = new Container();
      medallion.position.set(19, 22);
      medallion.addChild(new Graphics().circle(0, 0, 19).fill({ color: 0x000000, alpha: 0.3 }));
      row.addChild(medallion);
      const url = this.opts.slotIcon?.(node);
      if (url) {
        void Assets.load<Texture>(url)
          .then((t) => {
            if (medallion.destroyed) return;
            const s = new Sprite(t);
            s.anchor.set(0.5);
            s.scale.set(38 / Math.max(t.width, t.height));
            if (!open) s.alpha = 0.6;
            medallion.addChildAt(s, 1);
          })
          .catch(() => undefined);
      }
      if (!open && lock) {
        const badge = new Sprite(lock);
        badge.anchor.set(0.5);
        badge.scale.set(17 / Math.max(lock.width, lock.height));
        badge.position.set(13, 13);
        medallion.addChild(badge);
      }
      const name = new Text({
        text: node.name,
        style: uiStyle(15, open ? 0xffffff : 0xe2d8c8, '600'),
      });
      name.anchor.set(0, 0.5);
      name.position.set(48, 22);
      row.addChild(name);
      const button = new BrassButton({
        label: open ? 'PLAY' : String(slotGemPrice(node.id) ?? ''),
        kind: open ? 'gold' : 'wood',
        width: 84,
        height: 36,
        size: 16,
        onTap: () => this.playFromBoard(node),
      });
      if (!open && gem) {
        // The price reads as gems: a gem before the number.
        const g = new Sprite(gem);
        g.anchor.set(0.5);
        g.scale.set(16 / Math.max(gem.width, gem.height));
        g.position.set(24, 18);
        button.addChild(g);
        const caption = button.children.find((c): c is Text => c instanceof Text);
        if (caption) caption.x += 10;
      }
      button.label = `play:${node.name}`;
      button.position.set(width - 84, 4);
      row.addChild(button);
      const room = width - 84 - 58;
      if (name.width > room) name.scale.set(room / name.width);
      row.addChild(
        new Graphics().rect(0, ROW - 4, width, 1).fill({ color: COLOURS.brass, alpha: 0.25 }),
      );
      board.content.addChild(row);
      y += ROW;
    }
    const close = new BrassButton({
      label: 'Back to the map',
      kind: 'wood',
      width: Math.min(220, width),
      height: 40,
      size: 16,
      onTap: () => this.closeBoard(),
    });
    close.label = 'close';
    close.position.set((width - close.buttonWidth) / 2, y + 8);
    board.content.addChild(close);
    board.setContentHeight(y + 8 + 43);
    this.app.stage.addChild(board);
    this.boardReady = false;
    this.boardMoves.set(
      board,
      board.open().then(() => {
        if (this.board === board) this.boardReady = true;
      }),
    );
    // After open() has shown it: a hidden board doesn't cover the map.
    this.pauseAmbient();
  }

  /** A board in use: open, or opening. A closing board no longer counts. */
  protected boardUp(): boolean {
    return !!this.board?.visible && this.board.eventMode !== 'none';
  }

  /** Take a board off the stage, and destroy it once its animation ends. */
  private retireBoard(board: BoardDialog): void {
    board.eventMode = 'none';
    board.removeFromParent();
    void (this.boardMoves.get(board) ?? Promise.resolve()).then(() =>
      board.destroy({ children: true }),
    );
  }

  private playFromBoard(node: WorldMapNode): void {
    this.closeBoard();
    this.opts.onPlayNode(node);
  }

  protected closeBoard(): void {
    this.boardReady = false;
    const board = this.board;
    if (!board || board.eventMode === 'none') return;
    // Taps go to the map at once, not to the board fading out.
    board.eventMode = 'none';
    this.boardMoves.set(board, board.close());
    this.pauseAmbient();
  }

  private controls(): Map<string, Container> {
    const out = new Map<string, Container>();
    if (this.switcher) {
      out.set('prev', this.switcher.prev);
      out.set('next', this.switcher.next);
      out.set('world', this.switcher.centre);
    }
    const [friends, join, schedule] = this.social;
    if (friends) out.set('friends', friends);
    if (join) out.set('join', join);
    if (schedule) out.set('schedule', schedule);
    const [zoomOut, zoomIn] = this.zoomButtons;
    if (zoomOut) out.set('zoomOut', zoomOut);
    if (zoomIn) out.set('zoomIn', zoomIn);
    this.moreControls(out);
    if (this.board && this.boardUp()) {
      const walk = (c: Container): void => {
        if (c.label?.startsWith('play:') || c.label === 'close') out.set(c.label, c);
        for (const child of c.children) walk(child as Container);
      };
      walk(this.board.content);
    }
    return out;
  }

  // ---- input: drag to pan, pinch and the wheel to zoom ----

  private local(e: { clientX: number; clientY: number }): { x: number; y: number } {
    const r = this.app.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private pointerDown(e: FederatedPointerEvent): void {
    this.stopFly();
    const at = { x: e.global.x, y: e.global.y };
    if (this.pointers.size === 0) {
      this.dragged = false;
      this.downAt = at;
    }
    this.pointers.set(e.pointerId, at);
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()] as [
        { x: number; y: number },
        { x: number; y: number },
      ];
      if (this.allowsPinch()) {
        this.pinch = { d: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), zoom: this.cam.zoom };
      }
      this.dragged = true;
    }
  }

  private pointerMove(e: PointerEvent): void {
    const prev = this.pointers.get(e.pointerId);
    if (!prev) return;
    const at = this.local(e);
    this.pointers.set(e.pointerId, at);
    const down = this.downAt;
    if (down && Math.abs(at.x - down.x) + Math.abs(at.y - down.y) > DRAG_SLOP) {
      this.dragged = true;
      this.holdSwitcher = false;
    }
    if (this.pointers.size === 2 && this.pinch) {
      const [a, b] = [...this.pointers.values()] as [
        { x: number; y: number },
        { x: number; y: number },
      ];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      const factor = (this.pinch.zoom * d) / this.pinch.d / this.cam.zoom;
      this.apply(
        zoomAbout(this.cam, (a.x + b.x) / 2, (a.y + b.y) / 2, factor, this.map, this.view),
      );
      return;
    }
    if (this.pointers.size === 1 && this.dragged) {
      this.dragBy(at.x - prev.x, at.y - prev.y);
    }
  }

  private pointerUp(e: PointerEvent): void {
    const tracked = this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.pinch = null;
    if (this.pointers.size === 0) this.downAt = null;
    if (tracked && this.pointers.size === 0) this.pressEnded();
  }

  private wheel(e: WheelEvent): void {
    e.preventDefault();
    if (this.boardUp() || this.covering()) return;
    this.stopFly();
    this.holdSwitcher = false;
    const at = this.local(e);
    this.wheelAt(e, at);
  }
}

function rectOf(b: { x: number; y: number; width: number; height: number }): Rect {
  return { x: b.x, y: b.y, w: b.width, h: b.height };
}

/** A name's own box at screen size (scale 1), about its origin. */
export function boxOf(item: Container): Rect {
  return rectOf(item.getLocalBounds());
}
